'use client';

import { Close, Search } from '@shared/icons';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { type ComponentProps, useEffect, useRef, useState } from 'react';
import { cn } from '../../shadcn';

type MagazineSearchProps = Omit<ComponentProps<'form'>, 'action' | 'method' | 'onSubmit'> & {
  defaultQuery?: string;
};

const MAGAZINE_SEARCH_DEFAULT_PAGE = '1';

function MagazineSearch({ className, defaultQuery = '', ...props }: MagazineSearchProps) {
  const pathname = usePathname();
  const t = useTranslations('magazine.search');
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(defaultQuery);
  const hasQuery = query.length > 0;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(defaultQuery);
  }, [defaultQuery]);

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (pathname === ROUTES.MAGAZINE.SEARCH && defaultQuery.length > 0) {
      router.push(ROUTES.MAGAZINE.SEARCH);
    } else {
      setQuery('');
    }

    inputRef.current?.focus();
  };

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = query.trim();

    if (!trimmedQuery) return;

    router.push(`${ROUTES.MAGAZINE.SEARCH}?${new URLSearchParams({ q: trimmedQuery, page: MAGAZINE_SEARCH_DEFAULT_PAGE })}`);
  };

  return (
    <form
      {...props}
      action={ROUTES.MAGAZINE.SEARCH}
      method="get"
      className={cn('bg-background border-border box-border w-full rounded-lg border p-5', className)}
      onSubmit={handleSubmit}
      data-testid="magazine-search"
    >
      <input type="hidden" name="page" value={MAGAZINE_SEARCH_DEFAULT_PAGE} data-testid="magazine-search-hidden-page" />
      <div className="relative min-w-0" data-track-name="Search">
        <label className="bg-container border border-container flex w-full min-w-0 items-center gap-3 rounded-full pl-3 pr-12 py-2.5 focus-within:border-border-active cursor-text">
          <span className="sr-only" data-testid="magazine-search-label">
            {t('label')}
          </span>
          <Search size="medium" className="text-foreground shrink-0" data-testid="magazine-search-icon" />
          <input
            ref={inputRef}
            name="q"
            type="search"
            value={query}
            onChange={event => setQuery(event.currentTarget.value)}
            placeholder={t('placeholder')}
            className="text-foreground placeholder:text-foreground-subtle min-w-0 flex-1 appearance-none bg-transparent outline-none focus-visible:ring-0 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            data-testid="magazine-search-input"
          />
        </label>
        {hasQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-button-background-inverse-hover grid place-items-center size-6 rounded-full p-1"
            aria-label={t('clear_search')}
            data-testid="magazine-search-clear-button"
          >
            <Close size="extra-small" data-testid="magazine-search-clear-icon" />
          </button>
        )}
      </div>
      {/* Invisible submit button allows for submit via Enter key, or via soft keyboard */}
      <button type="submit" className="sr-only" tabIndex={-1} data-testid="magazine-search-submit">
        {t('submit_search')}
      </button>
    </form>
  );
}

export { MagazineSearch, type MagazineSearchProps };
